import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import { Box } from '@mui/material'
import React, { useMemo } from 'react'
import { getImportIdLabel } from '../utils/patientFormConfig'
import { usePatientFormI18n } from '../utils/patientFormI18n'
import { PatientAlternativeEmails } from './PatientAlternateContacts'
import PatientAttendants from './PatientAttendants'
import PatientCollapsibleSection from './PatientCollapsibleSection'
import PatientCommunicationToPref from './PatientCommunicationToPref'
import PatientDocumentsSection from './PatientDocumentsSection'
import {
  PATIENT_FORM_TOKENS as T,
  PatientCheckbox,
  PatientFormCell,
  PatientFormField,
  PatientFormGrid,
  PatientNativeSelect,
  PatientSectionTitle,
  PatientTextInput,
} from './PatientFormPrimitives'

const hasText = (value) => typeof value === 'string' && value.trim().length > 0
const iconSx = { fontSize: '18px', color: T.muted }

export function countFilledAddress(patientDetails = {}) {
  return ['address', 'pincode', 'state'].filter((key) => hasText(`${patientDetails?.[key] ?? ''}`)).length
}

export function countFilledAttendants(patientDetails = {}) {
  return (patientDetails?.attendants || []).filter((attendant) => hasText(attendant?.name)).length
}

export function countFilledOtherDetails(patientDetails = {}, { emailLivesHere = false } = {}) {
  let count = 0
  if (emailLivesHere) {
    if (hasText(patientDetails?.email)) count += 1
    count += (patientDetails?.altEmails || []).filter(hasText).length
  }
  ;['note', 'referredBy', 'uhId'].forEach((key) => {
    if (hasText(`${patientDetails?.[key] ?? ''}`)) count += 1
  })
  if (hasText(patientDetails?.importData?.importedFromId)) count += 1
  return count
}

/** Total optional details filled (all three groups). Kept for hosts/tests that only need a number. */
export function countFilledMoreDetails(patientDetails = {}, options = {}) {
  return (
    countFilledAddress(patientDetails) +
    countFilledAttendants(patientDetails) +
    countFilledOtherDetails(patientDetails, options)
  )
}

function GroupTitle({ children }) {
  return (
    <PatientFormCell span={12} sx={{ marginBottom: '-6px' }}>
      <PatientSectionTitle>{children}</PatientSectionTitle>
    </PatientFormCell>
  )
}

/**
 * The optional part of the form as three independent collapsible groups: Address, Attendants
 * and Other details. City stays in the essentials. Sections open on their own when they
 * already hold data, and are always open when viewing / editing an existing record.
 *
 * NOTE: no component is defined inside the render body (see the focus-loss bug this file
 * used to have).
 */
export const PatientMoreDetailsSection = ({
  patientDetails,
  editMode = true,
  formHook,
  validationHook,
  visibilityHook,
  makeAnonymousPatient,
  dontUseMoreDetailsAccordian = false,
  importIdFieldLabel,
  importedFromItems = [],
  identifiersInEssentials = false,
  showDocuments = true,
}) => {
  const { copy } = usePatientFormI18n()
  const disabled = !editMode
  const emailLivesHere = Boolean(visibilityHook.shouldShowEmailInMoreDetails)
  const alwaysOpen = dontUseMoreDetailsAccordian || !editMode || Boolean(patientDetails?.id)

  const addressCount = useMemo(() => countFilledAddress(patientDetails), [patientDetails])
  const attendantCount = useMemo(() => countFilledAttendants(patientDetails), [patientDetails])
  const otherCount = useMemo(
    () => countFilledOtherDetails(patientDetails, { emailLivesHere }),
    [patientDetails, emailLivesHere],
  )

  const emailValidation = validationHook.getFieldValidation('email')
  const attendantsVisible = visibilityHook.isFieldVisible('attendants')
  const attendantRows = Array.isArray(patientDetails?.attendants) ? patientDetails.attendants.length : 0
  const importSources = Array.isArray(importedFromItems) ? importedFromItems.filter(Boolean) : []
  const importSourceValue = patientDetails?.importData?.importedFrom || ''
  const importSourceLabel =
    importSources.find((item) => item?.id === importSourceValue || item?.value === importSourceValue)?.title ||
    importIdFieldLabel
  const addressSummary = [patientDetails?.address, patientDetails?.pincode].filter(hasText).join(' · ')
  const attendantSummary = (patientDetails?.attendants || [])
    .map((attendant) => attendant?.name)
    .filter(hasText)
    .join(', ')

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: alwaysOpen ? '4px' : '10px' }}>
      <PatientCollapsibleSection
        id="patient-address-section"
        title={copy.addressSectionTitle}
        icon={<HomeOutlinedIcon sx={iconSx} />}
        summary={addressSummary}
        count={addressCount}
        countLabel={copy.sectionCountLabel(addressCount)}
        defaultExpanded={addressCount > 0}
        alwaysOpen={alwaysOpen}
      >
        <PatientFormGrid>
          {visibilityHook.isFieldVisible('address') && (
            <PatientFormField label={copy.addressLabel} span={{ xs: 12, md: 8 }} htmlFor="patient-address">
              <PatientTextInput
                id="patient-address"
                autoComplete="off"
                value={patientDetails?.address || ''}
                onChange={formHook.handleAddressChange}
                disabled={disabled}
                placeholder={copy.addressPlaceholder}
              />
            </PatientFormField>
          )}
          {visibilityHook.isFieldVisible('pincode') && (
            <PatientFormField label={copy.pincodeLabel} span={{ xs: 5, md: 4 }} htmlFor="patient-pincode">
              <PatientTextInput
                id="patient-pincode"
                inputMode="numeric"
                autoComplete="off"
                value={patientDetails?.pincode || ''}
                onChange={formHook.handlePincodeChange}
                disabled={disabled}
                placeholder={copy.pincodePlaceholder}
              />
            </PatientFormField>
          )}
          <PatientFormField label={copy.stateLabel} span={{ xs: 7, md: 6 }} htmlFor="patient-state">
            <PatientTextInput
              id="patient-state"
              autoComplete="off"
              value={patientDetails?.state || ''}
              onChange={formHook.handleStateChange}
              disabled={disabled}
            />
          </PatientFormField>
        </PatientFormGrid>
      </PatientCollapsibleSection>

      {attendantsVisible && (
        <PatientCollapsibleSection
          id="patient-attendants-section"
          title={copy.attendantsSectionTitle}
          icon={<GroupsOutlinedIcon sx={iconSx} />}
          summary={attendantSummary}
          count={attendantCount}
          countLabel={copy.sectionCountLabel(attendantCount)}
          defaultExpanded={attendantCount > 0}
          alwaysOpen={alwaysOpen}
        >
          <PatientFormGrid>
            <PatientAttendants
              attendants={patientDetails?.attendants}
              onAdd={formHook.addAttendant}
              onUpdate={formHook.updateAttendant}
              onRemove={formHook.removeAttendant}
              editMode={editMode}
              validationHook={validationHook}
              showContactFields={!makeAnonymousPatient}
              title=" "
            />
            {visibilityHook.isFieldVisible('communicationToAttendant') && attendantRows > 0 && (
              <PatientFormCell span={12} sx={{ marginTop: '-4px' }}>
                <PatientCheckbox
                  id="patient-communication-to-attendant"
                  label={copy.communicationToAttendantLabel}
                  checked={Boolean(patientDetails?.communicationToAttendant)}
                  onChange={formHook.handleCommunicationToAttendantChange}
                  disabled={disabled}
                />
              </PatientFormCell>
            )}
          </PatientFormGrid>
        </PatientCollapsibleSection>
      )}

      {showDocuments && (
        <PatientDocumentsSection
          patientDetails={patientDetails}
          editMode={editMode}
          formHook={formHook}
          alwaysOpen={alwaysOpen}
        />
      )}

      <PatientCollapsibleSection
        id="patient-other-section"
        title={copy.otherDetailsSectionTitle}
        icon={<TuneOutlinedIcon sx={iconSx} />}
        count={otherCount}
        countLabel={copy.sectionCountLabel(otherCount)}
        defaultExpanded={otherCount > 0}
        alwaysOpen={alwaysOpen}
      >
        <PatientFormGrid>
          {emailLivesHere && (
            <>
              <GroupTitle>{copy.contactGroupTitle}</GroupTitle>
              <PatientFormField
                label={copy.emailLabel}
                span={{ xs: 12, md: 6 }}
                htmlFor="patient-email"
                error={emailValidation.showError ? emailValidation.errorMessage : null}
                footer={
                  visibilityHook.shouldShowAlternativeEmailsInMoreDetails ? (
                    <PatientAlternativeEmails
                      alternativeEmails={patientDetails?.altEmails}
                      onAdd={formHook.addAlternativeEmail}
                      onUpdate={formHook.updateAlternativeEmail}
                      onRemove={formHook.removeAlternativeEmail}
                      editMode={editMode}
                      validationHook={validationHook}
                    />
                  ) : null
                }
              >
                <PatientTextInput
                  id="patient-email"
                  type="email"
                  inputMode="email"
                  autoComplete="off"
                  value={patientDetails?.email || ''}
                  onChange={formHook.handleEmailChange}
                  onBlur={() => validationHook.markTouched('email')}
                  disabled={disabled}
                  invalid={emailValidation.showError}
                  placeholder={copy.emailPlaceholder}
                />
              </PatientFormField>
            </>
          )}

          <GroupTitle>{copy.preferencesGroupTitle}</GroupTitle>
          {visibilityHook.isFieldVisible('referredBy') && (
            <PatientFormField label={copy.referredByLabel} span={{ xs: 12, md: 4 }} htmlFor="patient-referred-by">
              <PatientTextInput
                id="patient-referred-by"
                autoComplete="off"
                value={patientDetails?.referredBy || ''}
                onChange={formHook.handleReferredByChange}
                disabled={disabled}
                placeholder={copy.referredByPlaceholder}
              />
            </PatientFormField>
          )}
          {visibilityHook.isFieldVisible('note') && (
            <PatientFormField label={copy.noteLabel} span={{ xs: 12, md: 4 }} htmlFor="patient-note">
              <PatientTextInput
                id="patient-note"
                autoComplete="off"
                value={patientDetails?.note || ''}
                onChange={formHook.handleNoteChange}
                disabled={disabled}
                placeholder={copy.notePlaceholder}
              />
            </PatientFormField>
          )}

          {!identifiersInEssentials && (
            <>
              <GroupTitle>{copy.identifiersGroupTitle}</GroupTitle>
              <PatientFormField label={copy.uhidLabel} span={{ xs: 12, md: 4 }} htmlFor="patient-uhid">
                <PatientTextInput
                  id="patient-uhid"
                  autoComplete="off"
                  value={patientDetails?.uhId || ''}
                  onChange={formHook.handleUhidChange}
                  disabled={disabled}
                />
              </PatientFormField>
              {importSources.length > 0 && (
                <PatientFormField label={copy.importSourceLabel} span={{ xs: 12, md: 4 }} htmlFor="patient-import-source">
                  <PatientNativeSelect
                    id="patient-import-source"
                    value={importSourceValue}
                    onChange={formHook.handleImportSourceChange}
                    disabled={disabled}
                    getOptionValue={(option) => option?.id ?? option?.value ?? ''}
                    getOptionLabel={(option) => option?.title ?? option?.label ?? option?.id ?? ''}
                    options={[{ id: '', title: '—' }, ...importSources]}
                  />
                </PatientFormField>
              )}
              <PatientFormField
                label={importSourceLabel ? getImportIdLabel(importSourceLabel) : copy.importIdLabel}
                span={{ xs: 12, md: 4 }}
                htmlFor="patient-import-id"
              >
                <PatientTextInput
                  id="patient-import-id"
                  autoComplete="off"
                  value={patientDetails?.importData?.importedFromId || ''}
                  onChange={formHook.handleImportIdChange}
                  disabled={disabled}
                />
              </PatientFormField>
            </>
          )}

          {visibilityHook.isFieldVisible('communicationToPref') && (
            <>
              <GroupTitle>{copy.adminGroupTitle}</GroupTitle>
              <PatientCommunicationToPref
                communicationToPref={patientDetails?.communicationToPref}
                onPreferenceChange={formHook.handleCommunicationToPrefChange}
                onAddReceiver={formHook.addCommunicationReceiver}
                onUpdateReceiver={formHook.updateCommunicationReceiver}
                onRemoveReceiver={formHook.removeCommunicationReceiver}
                editMode={editMode}
                validationHook={validationHook}
              />
            </>
          )}
        </PatientFormGrid>
      </PatientCollapsibleSection>
    </Box>
  )
}

export default PatientMoreDetailsSection
